import React, {
  ChangeEvent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { Input } from "components/beta/Input";
import Text, { TextType } from "components/Text";
import { BrandColorPalette } from "lib/styles/colors";
import Checkmark from "lib/svgs/Checkmark";
import { useAppValues } from "lib/context/AppContext";
import { ByStringId } from "lib/types/common";
import { checkObjectHasKeys } from "lib/helpers/checkObjectHasKeys";
import { OneOffCredTypes } from "../types";

export interface CredentialsListWithHeader {
  title: string;
  data: string[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 640px;
`;

const SearchContainer = styled.div`
  width: 100%;
  /* height: 48px; */
  align-items: center;
`;

const DropdownWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  border: 1px solid #e8e8e8;
`;

const ForceCloseButton = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 8px;
  cursor: pointer;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  padding: 8px;
  border-bottom: ${BrandColorPalette.BRANDING} 1px solid;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  overflow-y: scroll;
  max-height: 200px;
  z-index: 100;
`;

interface ItemStyleProps {
  isSelected?: boolean;
}
const StyledDropdownItem = styled.div<ItemStyleProps>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  padding: 8px;
  background-color: ${(props) => (props.isSelected ? "#f8f8f8" : "white")};
  &:hover {
    background-color: ${(props) => (props.isSelected ? "#f8f8f8" : "#e8e8e8")};
    cursor: ${(props) => (props.isSelected ? "not-allowed" : "pointer")};
  }
`;

const CheckmarkContainer = styled.div`
  margin-right: 12px;
`;

interface Props {
  selectedCredKeyName: string | null;
  setSelectedCredKeyName: React.Dispatch<React.SetStateAction<string | null>>;
}

export default function AddCredentialTypeInputSelect({
  selectedCredKeyName,
  setSelectedCredKeyName,
}: Props): JSX.Element {
  const { getAppValues } = useAppValues();
  const [searchValue, setSearchValue] = useState<string>("");
  const {
    currentUserCredentials,
    organizationRequiredCredentials,
    supportedCredentialTitles,
    countryAlpha2,
  } = getAppValues();
  const { formattedSectionList, hasOrgCreds } = useMemo(() => {
    return formatDataIntoSectionList({
      countryAlpha2,
      organizationRequiredCredentials,
      searchValue: selectedCredKeyName ?? searchValue,
      supportedCredentialTitles,
    });
  }, [
    organizationRequiredCredentials?.length,
    supportedCredentialTitles?.length,
    countryAlpha2,
    searchValue,
    selectedCredKeyName,
  ]);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [blurTimeoutId, setBlurTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (blurTimeoutId) {
        clearTimeout(blurTimeoutId);
      }
    };
  }, [blurTimeoutId]);

  const handleFocus = () => {
    if (blurTimeoutId) {
      clearTimeout(blurTimeoutId);
      setBlurTimeoutId(null);
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    // setIsFocused(false);
    // Add delay to blur event
    const timeoutId: NodeJS.Timeout = setTimeout(() => {
      setIsFocused(false);
      setBlurTimeoutId(null);
    }, 200); // 200 ms delay
    setBlurTimeoutId(timeoutId);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setSelectedCredKeyName(null);
  };

  const handleSelectCredential = (credKeyName: string) => {
    setSearchValue(credKeyName);
    setSelectedCredKeyName(credKeyName);
    handleBlur();
  };

  return (
    <Container>
      <SearchContainer>
        <Input
          placeholder={"Search By Credential Name"}
          error={false}
          width="640px"
          value={searchValue ?? undefined}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </SearchContainer>
      {isFocused && (
        <DropdownWrapper>
          <>
            {formattedSectionList.map((section, index) => {
              const formattedTitle = getFormattedTitle(
                section.title,
                hasOrgCreds
              );
              return (
                <DropdownSection
                  key={`${formattedTitle}-${index}`}
                  title={formattedTitle}
                  options={section.data}
                  currentUserCredentials={currentUserCredentials}
                  handleSelectCredential={handleSelectCredential}
                />
              );
            })}
            <ForceCloseButton onClick={() => setIsFocused(false)}>
              <Text type={TextType.PARAGRAPH}>Close</Text>
            </ForceCloseButton>
          </>
        </DropdownWrapper>
      )}
    </Container>
  );
}

interface SectionProps {
  title: string;
  options: string[];
  currentUserCredentials: string[];
  handleSelectCredential: (credKeyName: string) => void;
}
function DropdownSection({
  title,
  options,
  currentUserCredentials,
  handleSelectCredential,
}: SectionProps): ReactElement {
  return (
    <HeaderWrapper>
      <Text type={TextType.PARAGRAPH} fontColor={BrandColorPalette.BRANDING}>
        {title}
      </Text>
      {options && options.length > 0 && (
        <DropdownContainer>
          {options.map((option, index) => {
            const isInCurrentCredentials = (
              currentUserCredentials ?? []
            ).includes(option);
            return (
              <DropdownItem
                key={`${option}-${index}`}
                credKeyName={option}
                isSelected={isInCurrentCredentials}
                handleItemClick={handleSelectCredential}
              />
            );
          })}
        </DropdownContainer>
      )}
    </HeaderWrapper>
  );
}

interface ItemProps {
  isSelected: boolean;
  credKeyName: string;
  handleItemClick: (credKeyName: string) => void;
}

function DropdownItem({
  isSelected,
  credKeyName,
  handleItemClick,
}: ItemProps): ReactElement {
  const handleSelect = () => {
    if (isSelected) {
      return;
    }
    handleItemClick(credKeyName);
  };
  return (
    <StyledDropdownItem
      isSelected={isSelected}
      key={credKeyName}
      onClick={handleSelect}
    >
      {isSelected && (
        <CheckmarkContainer>
          <Checkmark />
        </CheckmarkContainer>
      )}
      <Text type={TextType.PARAGRAPH}>{credKeyName}</Text>
    </StyledDropdownItem>
  );
}

function getFormattedTitle(title: string, hasOrgCreds: boolean): string {
  if (!hasOrgCreds) {
    return "Supported Credentials";
  }
  if (title === "Others") {
    return "Others";
  }
  return `Required by ${title}`;
}

/**
 * Helper function to split the credentials into sections based on required by Organizations
 * @param options
 * @returns
 */
function formatDataIntoSectionList(options: {
  organizationRequiredCredentials: ByStringId<string[]>;
  supportedCredentialTitles: string[];
  countryAlpha2: string | null;
  searchValue: string;
}): {
    formattedSectionList: CredentialsListWithHeader[];
    fullTitlesList: string[];
    hasOrgCreds: boolean;
  } {
  const {
    organizationRequiredCredentials,
    supportedCredentialTitles,
    countryAlpha2,
    searchValue,
  } = options;
  const formattedSearchValue = searchValue
    ? searchValue.trim().toLowerCase()
    : "";
  const hasOrgCreds = checkObjectHasKeys(organizationRequiredCredentials);
  let allTitles = [...supportedCredentialTitles, OneOffCredTypes.OTHER];
  if (countryAlpha2 && countryAlpha2.toLowerCase() === "us") {
    allTitles = [OneOffCredTypes.COVID, ...allTitles];
  }
  let fullTitlesList = [...allTitles];

  const output: CredentialsListWithHeader[] = [];
  /**
   * @step1 - map through each key in organizationRequiredCredentials
   * make that a Title, and the names values
   */
  for (const [orgName, credNames] of Object.entries(
    organizationRequiredCredentials
  )) {
    const filteredCredNames = searchValue
      ? credNames.filter((currName) =>
        currName.trim().toLowerCase().includes(formattedSearchValue)
      )
      : credNames;
    const current: CredentialsListWithHeader = {
      data: filteredCredNames,
      title: orgName,
    };
    fullTitlesList = [...fullTitlesList, ...credNames];
    output.push(current);
  }
  /**
   * @step2 -- assing the  supportedCredentialTitles to Other header
   */
  const filteredTitlesNames = searchValue
    ? allTitles.filter((currName) =>
      currName.trim().toLowerCase().includes(formattedSearchValue)
    )
    : allTitles;
  output.push({
    data: filteredTitlesNames,
    title: "Others",
  });
  return {
    formattedSectionList: output,
    fullTitlesList,
    hasOrgCreds,
  };
}
